import { apiSlice } from './apiSlice';

export const adminApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    // --- Content Management ---
    createContent: builder.mutation({
      query: (data) => ({ url: 'admin/content', method: 'POST', data }),
      invalidatesTags: ['Content'],
    }),

    listContent: builder.query({
      query: (params = {}) => ({
        url: 'admin/content',
        method: 'GET',
        params,
      }),
      providesTags: ['Content'],
    }),

    getContentById: builder.query({
      query: (id) => ({ url: `admin/content/${id}`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'Content', id }],
    }),

    updateContent: builder.mutation({
      query: (data) => ({ url: 'admin/content', method: 'PUT', data }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Content', id: arg._id },
        'Content',
        'Homepage'
      ],
    }),

    deleteContent: builder.mutation({
      query: (id) => ({ url: `admin/content/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Content', 'Homepage'],
    }),

    // --- Section Management ---
    createSection: builder.mutation({
      query: (data) => ({ url: 'admin/sections', method: 'POST', data }),
      invalidatesTags: ['Section', 'Homepage'],
    }),

    listSections: builder.query({
      query: ({ sectionType } = {}) => ({
        url: 'admin/sections',
        method: 'GET',
        params: { sectionType },
      }),
      providesTags: ['Section'],
    }),

    updateSection: builder.mutation({
      query: (data) => ({ url: 'admin/sections', method: 'PUT', data }),
      invalidatesTags: ['Section', 'Homepage'],
    }),

    deleteSection: builder.mutation({
      query: (id) => ({ url: `admin/sections/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Section', 'Homepage'],
    }),

    // --- Row Management ---
    listRows: builder.query({
      query: () => ({ url: 'admin/rows', method: 'GET' }),
      providesTags: ['Row'],
    }),

    createRow: builder.mutation({
      query: (data) => ({ url: 'admin/rows', method: 'POST', data }),
      invalidatesTags: ['Row', 'Homepage'],
    }),

    updateRow: builder.mutation({
      query: (data) => ({ url: 'admin/rows', method: 'PUT', data }),
      invalidatesTags: ['Row', 'Homepage'],
    }),

    // Specifically matches Postman structure { "rowOrders": [...] }
    reorderRows: builder.mutation({
      query: (rowOrdersArray) => ({
        url: 'admin/rows/reorder',
        method: 'PUT',
        data: { rowOrders: rowOrdersArray },
      }),
      invalidatesTags: ['Row', 'Homepage'],
    }),

    // Specifically matches Postman structure { "sections": [...] }
    updateRowSections: builder.mutation({
      query: ({ rowId, sections }) => ({
        url: `admin/rows/${rowId}/sections`,
        method: 'PUT',
        data: { sections },
      }),
      invalidatesTags: ['Row', 'Homepage'],
    }),

    deleteRow: builder.mutation({
      query: (rowId) => ({ url: `admin/rows/${rowId}`, method: 'DELETE' }),
      invalidatesTags: ['Row', 'Homepage'],
    }),

    listBlogs: builder.query({
      query: () => ({ url: 'admin/blogs', method: 'GET' }),
      providesTags: ['Blog'],
    }),

    listContentByWidgetType: builder.query({
      query: ({ widgetType, page = 1, limit = 30 } = {}) => ({
        url: `admin/content/by-widget-type/${widgetType}`,
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['Content'],
    }),

    getCrawlerDetails: builder.query({
      query: (id) => ({ url: `admin/crawlers/${id}`, method: 'GET' }),
      providesTags: (result, error, id) => [{ type: 'UserDynamic', id }],
    }),
    listTags: builder.query({
      query: (params = {}) => ({ url: 'admin/tags', method: 'GET', params }),
      providesTags: ['Tag'],
    }),

    // --- Perk Management ---
    listPerkSections: builder.query({
      query: (params = {}) => ({ url: 'admin/perks/sections', method: 'GET', params }),
      providesTags: ['Perks'],
    }),

    createPerkSection: builder.mutation({
      query: (data) => ({ url: 'admin/perks/sections', method: 'POST', data }),
      invalidatesTags: ['Perks'],
    }),

    updatePerkSection: builder.mutation({
      query: ({ id, ...data }) => ({ url: `admin/perks/sections/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['Perks'],
    }),

    deletePerkSection: builder.mutation({
      query: (id) => ({ url: `admin/perks/sections/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Perks'],
    }),

    listPerkCards: builder.query({
      query: (sectionId) => ({ url: `admin/perks/sections/${sectionId}/cards`, method: 'GET' }),
      providesTags: ['Perks'],
    }),

    replacePerkCards: builder.mutation({
      query: ({ sectionId, cards }) => ({
        url: `admin/perks/sections/${sectionId}/cards`,
        method: 'PUT',
        data: { cards },
      }),
      invalidatesTags: ['Perks'],
    }),

    updatePerkCard: builder.mutation({
      query: ({ id, ...data }) => ({ url: `admin/perks/cards/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['Perks'],
    }),

    deletePerkCard: builder.mutation({
      query: (id) => ({ url: `admin/perks/cards/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Perks'],
    }),

    // --- PA Analyses Management ---
    getNextProduct: builder.query({
      query: (params = {}) => ({ url: 'admin/pa-analyses/next-product', method: 'GET', params }),
      providesTags: ['PA_Analysis'],
    }),
    getPAAnalysesStats: builder.query({
      query: () => ({ url: 'admin/pa-analyses/stats', method: 'GET' }),
      providesTags: ['PA_Analysis_Stats'],
    }),
    upsertPAAnalysis: builder.mutation({
      query: (data) => ({ url: 'admin/pa-analyses', method: 'POST', data }),
      invalidatesTags: ['PA_Analysis_Stats'],
    }),
    patchPAAnalysis: builder.mutation({
      query: ({ id, ...data }) => ({ url: `admin/pa-analyses/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['PA_Analysis'],
    }),
    getUserPAAnalyses: builder.query({
      query: (params = {}) => ({ url: 'admin/pa-analyses/user', method: 'GET', params }),
      providesTags: ['PA_Analysis'],
    }),
    getVendorPricingSchedule: builder.query({
      query: ({ page = 1, limit = 50, search = "" } = {}) => ({
        url: 'admin/vendor-pricing-schedule',
        method: 'GET',
        params: { page, limit, search },
      }),
      providesTags: ['VendorPricingSchedule'],
    }),
    saveVendorPricingSchedule: builder.mutation({
      query: ({ vendorId, slots }) => ({
        url: `admin/vendor-pricing-schedule/${vendorId}`,
        method: 'PUT',
        data: { slots },
      }),
      invalidatesTags: ['VendorPricingSchedule'],
    }),
    saveVendorPricingSlot: builder.mutation({
      query: ({ vendorId, day, time, pricingType, showCoupon }) => ({
        url: `admin/vendor-pricing-schedule/${vendorId}/slot`,
        method: 'PATCH',
        data: { day, time, pricingType, showCoupon },
      }),
      invalidatesTags: ['VendorPricingSchedule'],
    }),
  }),
});


export const {
  useCreateContentMutation,
  useListContentQuery,
  useListContentByWidgetTypeQuery,
  useGetContentByIdQuery,
  useLazyGetContentByIdQuery,
  useUpdateContentMutation,
  useDeleteContentMutation,
  useCreateSectionMutation,
  useListSectionsQuery,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useListRowsQuery,
  useCreateRowMutation,
  useUpdateRowMutation,
  useReorderRowsMutation,
  useUpdateRowSectionsMutation,
  useDeleteRowMutation,
  useGetCrawlerDetailsQuery,
  useListBlogsQuery,
  useListTagsQuery,
  useListPerkSectionsQuery,
  useCreatePerkSectionMutation,
  useUpdatePerkSectionMutation,
  useDeletePerkSectionMutation,
  useListPerkCardsQuery,
  useReplacePerkCardsMutation,
  useUpdatePerkCardMutation,
  useDeletePerkCardMutation,
  useGetNextProductQuery,
  useLazyGetNextProductQuery,
  useGetPAAnalysesStatsQuery,
  useUpsertPAAnalysisMutation,
  usePatchPAAnalysisMutation,
  useGetUserPAAnalysesQuery,
  useLazyGetUserPAAnalysesQuery,
  useGetVendorPricingScheduleQuery,
  useSaveVendorPricingScheduleMutation,
  useSaveVendorPricingSlotMutation,
} = adminApi;
